import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import mermaid from 'mermaid';
import {
  Scale,
  GitBranch,
  Clock,
  Users,
  Zap,
  FileText,
  Share2,
  Calendar,
  AlertCircle,
  Loader2,
  User,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#1e3a5f',
    primaryTextColor: '#fff',
    primaryBorderColor: '#2d4a6f',
    lineColor: '#64748b',
    secondaryColor: '#c9a962',
    tertiaryColor: '#f8fafc'
  },
  flowchart: {
    htmlLabels: true,
    curve: 'basis',
    rankSpacing: 50,
    nodeSpacing: 50
  }
});

export default function SharedWorkflowPage() {
  const { token } = useParams();
  const mermaidRef = useRef(null);
  
  const [workflow, setWorkflow] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mermaidSyntax, setMermaidSyntax] = useState('');

  useEffect(() => {
    fetchSharedWorkflow();
  }, [token]);

  useEffect(() => {
    if (workflow && mermaidRef.current) {
      generateMermaidSyntax();
    }
  }, [workflow]);

  useEffect(() => {
    if (mermaidSyntax && mermaidRef.current) {
      renderMermaid();
    }
  }, [mermaidSyntax]);

  const fetchSharedWorkflow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/workflow/shared/${token}`);
      
      if (res.ok) {
        const data = await res.json();
        setWorkflow(data.workflow);
        setShareInfo(data.share_info);
      } else if (res.status === 404) {
        setError('This share link is invalid or has been deleted.');
      } else if (res.status === 410) {
        setError('This share link has expired.');
      } else {
        setError('Failed to load the shared workflow.');
      }
    } catch (err) {
      setError('Failed to connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  const generateMermaidSyntax = () => {
    if (!workflow) return;

    const lines = ['flowchart TD'];
    
    const shapeMap = {
      start: ['([', '])'],
      end: ['([', '])'],
      task: ['[', ']'],
      decision: ['{', '}'],
      document: ['[[', ']]'],
      email: ['>', ']'],
      approval: ['{{', '}}'],
      deadline: ['((', '))'],
      integration: ['[(', ')]']
    };

    const styleClasses = [];
    const roleFilter = shareInfo?.role_filter?.toLowerCase();

    // Add nodes
    for (const node of workflow.nodes || []) {
      const nodeId = (node.id || '').replace(/-/g, '_').replace(/ /g, '_');
      const nodeType = node.type || 'task';
      const label = (node.label || 'Node').replace(/"/g, "'").replace(/\[/g, '(').replace(/\]/g, ')');
      const [open, close] = shapeMap[nodeType] || ['[', ']'];
      
      lines.push(`    ${nodeId}${open}"${label}"${close}`);
      
      // Style for role highlighting
      if (roleFilter && node.role?.toLowerCase() === roleFilter) {
        styleClasses.push(`class ${nodeId} highlightedNode`);
      } else if (nodeType === 'start') {
        styleClasses.push(`class ${nodeId} startNode`);
      } else if (nodeType === 'end') {
        styleClasses.push(`class ${nodeId} endNode`);
      } else if (nodeType === 'decision') {
        styleClasses.push(`class ${nodeId} decisionNode`);
      }
    }

    // Add edges
    for (const edge of workflow.edges || []) {
      const source = (edge.source || '').replace(/-/g, '_').replace(/ /g, '_');
      const target = (edge.target || '').replace(/-/g, '_').replace(/ /g, '_');
      const label = edge.label ? `|"${edge.label.replace(/"/g, "'")}"| ` : '';
      
      lines.push(`    ${source} -->${label}${target}`);
    }

    // Add styles
    lines.push('');
    lines.push('    %% Styles');
    lines.push('    classDef startNode fill:#22c55e,stroke:#16a34a,color:#fff');
    lines.push('    classDef endNode fill:#ef4444,stroke:#dc2626,color:#fff');
    lines.push('    classDef decisionNode fill:#f59e0b,stroke:#d97706,color:#fff');
    lines.push('    classDef highlightedNode fill:#8b5cf6,stroke:#7c3aed,color:#fff,stroke-width:3px');
    
    for (const styleClass of styleClasses) {
      lines.push(`    ${styleClass}`);
    }

    setMermaidSyntax(lines.join('\n'));
  };

  const renderMermaid = async () => {
    if (!mermaidRef.current || !mermaidSyntax) return;
    
    try {
      mermaidRef.current.innerHTML = '';
      const { svg } = await mermaid.render('shared-workflow-diagram', mermaidSyntax);
      mermaidRef.current.innerHTML = svg;
    } catch (error) {
      console.error('Mermaid render error:', error);
    }
  };

  const getAutomationIcon = (type) => {
    switch (type) {
      case 'document_generation': return FileText;
      case 'email_notification': return Share2;
      case 'deadline_reminder': return Clock;
      case 'calendar_event': return Calendar;
      case 'task_assignment': return Users;
      default: return Zap;
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-navy mx-auto mb-4" />
          <p className="text-muted-foreground">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Unable to Load Workflow</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>Go to Homepage</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="shared-workflow-page">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
                <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
              </div>
            </div>
            
            <Badge variant="outline" className="text-xs">
              <GitBranch className="w-3 h-3 mr-1" />
              Workflow.AI
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Shared Info Banner */}
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
              <User className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Shared by {shareInfo?.shared_by}</p>
              <p className="text-xs text-muted-foreground">
                {shareInfo?.created_at && `Shared on ${new Date(shareInfo.created_at).toLocaleDateString()}`}
              </p>
            </div>
          </div>
          {shareInfo?.role_filter && (
            <Badge className="bg-violet-600">
              <CheckCircle className="w-3 h-3 mr-1" />
              {shareInfo.role_filter} View
            </Badge>
          )}
        </div>

        {/* Workflow Header */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">{workflow.name}</CardTitle>
            <CardDescription className="text-base">
              {workflow.summary || workflow.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              {workflow.estimated_total_time && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{workflow.estimated_total_time}</span>
                </div>
              )}
              {workflow.roles_identified?.length > 0 && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span>{workflow.roles_identified.join(', ')}</span>
                </div>
              )}
              {workflow.automation_points?.length > 0 && (
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>{workflow.automation_points.length} automation opportunities</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role-specific notice */}
        {shareInfo?.role_filter && (
          <div className="bg-violet-100 border border-violet-300 rounded-lg p-4 mb-6">
            <p className="text-sm text-violet-800">
              <strong>Note:</strong> Steps highlighted in purple are specifically relevant to your role as <strong>{shareInfo.role_filter}</strong>.
            </p>
          </div>
        )}

        {/* Workflow Diagram */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-violet-600" />
              Workflow Diagram
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              ref={mermaidRef} 
              className="bg-slate-50 rounded-lg p-6 overflow-auto min-h-[400px] flex items-center justify-center"
            >
              {!mermaidSyntax && (
                <div className="text-center text-muted-foreground">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p>Rendering diagram...</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Steps for this role (if role filter applied) */}
        {shareInfo?.role_filter && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-violet-600" />
                Your Responsibilities ({shareInfo.role_filter})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {workflow.nodes
                  ?.filter(n => n.role?.toLowerCase() === shareInfo.role_filter.toLowerCase())
                  .map((node, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                      <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="font-medium">{node.label}</h4>
                        {node.description && (
                          <p className="text-sm text-muted-foreground mt-1">{node.description}</p>
                        )}
                        {node.estimated_time && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {node.estimated_time}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Automation Opportunities */}
        {workflow.automation_points?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Automation Opportunities
              </CardTitle>
              <CardDescription>
                AI-identified points where automation can improve efficiency
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {workflow.automation_points.map((ap, idx) => {
                  const Icon = getAutomationIcon(ap.type);
                  return (
                    <Card key={idx} className="bg-slate-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-sm">{ap.title}</h4>
                              <Badge className={`text-xs ${getImpactColor(ap.impact)}`}>
                                {ap.impact}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {ap.description}
                            </p>
                            {ap.estimated_time_saved && (
                              <p className="text-xs font-medium text-emerald-600">
                                Time Saved: {ap.estimated_time_saved}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Powered by Legal AIDynamics
            </p>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              Learn More
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
